import { useId, useRef } from "react";
import type { ReactElement } from "react";

import { Button } from "../atoms/Button";
import { useModalFocus } from "../useModalFocus";
import styles from "./ConfirmDialog.module.css";

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /** The confirm button is busy (refuses a press, stays focusable) while something it depends on loads. */
  readonly confirmLoading?: boolean | undefined;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * The replacement for `confirm()`, which ESLint forbids.
 *
 * A native `confirm()` blocks the whole thread, cannot be styled, cannot be
 * translated and cannot be tested; this one is a `role="dialog"` with
 * `aria-modal`, focus trapped inside it and returned to whatever had it when
 * the dialog closes (`useModalFocus`, shared with `Dialog` and `Drawer`).
 *
 * The initial focus goes to CANCEL, not to the confirm button. The dialog only
 * ever appears in front of a destructive action, so the safe answer is the one
 * that a stray Enter should give.
 *
 * It wears the redesign mockup's confirm dialog: scrim, 560px card, title,
 * muted body and a footer row under a hairline.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  confirmLoading,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useModalFocus(open, dialogRef, onCancel);

  if (!open) return null;

  return (
    <div className={styles.backdrop}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
      >
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <p id={bodyId} className={styles.body}>
          {body}
        </p>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="danger" loading={confirmLoading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
